import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AuthContext from '../../contexts/AuthContext';
import EditUserRoleDialog from './EditUserRoleDialog';

/**
 * Displays a paginated, searchable list of users for the given organization.
 * Props:
 *  - organizationId: UUID of the organization to load users for
 */
export default function UsersTabContent({ organizationId, organization }) {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens, authData } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (!tokens?.id_token || !organizationId) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (searchText) params.append('search_text', searchText);
    if (selectedJob) params.append('job', selectedJob);
    params.append('skip', page * rowsPerPage);
    params.append('limit', rowsPerPage);
    fetch(`${apiBase}/organizations/${organizationId}/users?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch users');
        return res.json();
      })
      .then(data => {
        setUsers(data.items || []);
        setTotal(data.total || 0);
        setError(null);
      })
      .catch(err => {
        setError(err.message || 'Failed to fetch users');
      })
      .finally(() => setLoading(false));
  }, [tokens, organizationId, page, rowsPerPage, searchText, selectedJob]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // Debounce search input similar to Organizations page
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchText(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);
  const handleJobChange = (event) => {
    setSelectedJob(event.target.value);
    setPage(0);
  };
  const handleRemove = async (jobAtOrgId) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) return;
    try {
      setLoading(true);
      const res = await fetch(
        `${apiBase}/organizations/${organizationId}/users/${jobAtOrgId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${tokens.id_token}` },
        }
      );
      if (!res.ok) throw new Error('Failed to remove user');
      setUsers(prev => prev.filter((u) => u.job_at_org_id !== jobAtOrgId));
      setTotal(prev => prev - 1);
    } catch (err) {
      window.alert(err.message || 'Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setEditOpen(true);
  };
  const closeEdit = () => {
    setEditOpen(false);
    setEditingUser(null);
  };
  const handleUpdated = (updated) => {
    setUsers(prev => prev.map(u => (u.job_at_org_id === updated.job_at_org_id ? updated : u)));
  };

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search Users"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name or email"
          sx={{ width: 300 }}
          InputProps={{
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="job-filter-label">Job</InputLabel>
          <Select
            labelId="job-filter-label"
            value={selectedJob}
            label="Job"
            onChange={handleJobChange}
          >
            <MenuItem value="">All Jobs</MenuItem>
            <MenuItem value="Provider">Provider</MenuItem>
            <MenuItem value="OfficeStaff">OfficeStaff</MenuItem>
            <MenuItem value="Phlebotomist">Phlebotomist</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Paper>
        <TableContainer>
          {error ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(user => {
                  const isSelf = authData?.user?.id && user.id === authData.user.id;
                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.job}</TableCell>
                      <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openEdit(user)} sx={{ mr: 1 }} title={'Edit job'}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleRemove(user.job_at_org_id)} disabled={isSelf} title={isSelf ? 'You cannot remove yourself from this organization' : 'Remove from organization'}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <EditUserRoleDialog
        open={editOpen}
        onClose={closeEdit}
        organizationId={organizationId}
        organization={organization}
        user={editingUser}
        onUpdated={handleUpdated}
      />
    </>
  );
}
